"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import WaveApprovalStatusBar from "@/components/waves/approval/WaveApprovalStatusBar";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface CreateDropUiState {
  readonly waveId: string;
  readonly submissionExperience: WaveSubmissionExperience | null;
  readonly isApprovalVotingControlsLocked: boolean;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(118);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isApproveWave,
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submissionExperience = t2;
  const mountedRef = useRef(true);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      mountedRef.current = false;
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const [minPrice, setMinPrice] = useState(undefined);
  const [maxPrice, setMaxPrice] = useState(undefined);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[7] !== isCurationWave || $[8] !== isLoggedIn || $[9] !== participation || $[10] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[7] = isCurationWave;
    $[8] = isLoggedIn;
    $[9] = participation;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    canCreateDrop
  } = t6;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  let t7;
  if ($[12] !== isCurationWave) {
    t7 = value => value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT || isCurationWave && value === WaveDropsLeaderboardSort.PRICE;
    $[12] = isCurationWave;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, t7);
  const t8 = wave.wave.type !== ApiWaveType.Chat;
  let t9;
  if ($[14] !== t8 || $[15] !== wave.id) {
    t9 = {
      waveId: wave.id,
      enabled: t8
    };
    $[14] = t8;
    $[15] = wave.id;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    data: t10,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurations(t9);
  let t11;
  if ($[17] !== t10) {
    t11 = t10 === undefined ? [] : t10;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const curationGroups = t11;
  let t12;
  if ($[19] !== wave) {
    t12 = {
      wave
    };
    $[19] = wave;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const {
    approvedCount,
    closeStatus: approvalCloseStatus,
    isApprovalStatusError,
    isVotingClosed: isApprovalVotingClosed,
    isVotingControlsLocked: isApprovalVotingControlsLocked,
    retryApprovalStatus
  } = useApprovalWaveStatus(t12);
  const canOpenCreateDrop = canCreateDrop && !isApprovalVotingControlsLocked;
  let t13;
  if ($[21] !== isApprovalVotingControlsLocked || $[22] !== wave.id) {
    t13 = () => ({
      waveId: wave.id,
      submissionExperience: null,
      isApprovalVotingControlsLocked
    });
    $[21] = isApprovalVotingControlsLocked;
    $[22] = wave.id;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const [createDropUiState, setCreateDropUiState] = useState(t13);
  const activeCreateDropExperience = canOpenCreateDrop && createDropUiState.waveId === wave.id && createDropUiState.submissionExperience === submissionExperience && createDropUiState.isApprovalVotingControlsLocked === isApprovalVotingControlsLocked ? createDropUiState.submissionExperience : null;
  const showToggleableDropInput = activeCreateDropExperience !== null && activeCreateDropExperience !== WaveSubmissionExperience.MEMES_LEGACY && activeCreateDropExperience !== WaveSubmissionExperience.CURATION_LEGACY && activeCreateDropExperience !== WaveSubmissionExperience.QUORUM_PROPOSAL;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      if (!mountedRef.current) {
        return;
      }
      setCreateDropUiState(_temp);
    };
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const closeCreateDrop = t14;
  let t15;
  if ($[25] !== canOpenCreateDrop || $[26] !== isApprovalVotingControlsLocked || $[27] !== submissionExperience || $[28] !== wave.id) {
    t15 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (!canOpenCreateDrop) {
        return;
      }
      setCreateDropUiState({
        waveId: wave.id,
        submissionExperience,
        isApprovalVotingControlsLocked
      });
    };
    $[25] = canOpenCreateDrop;
    $[26] = isApprovalVotingControlsLocked;
    $[27] = submissionExperience;
    $[28] = wave.id;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const onCreateDrop = t15;
  let t16;
  if ($[30] !== searchParams) {
    t16 = searchParams.get("curation_id");
    $[30] = searchParams;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const rawCuratedByGroupId = t16;
  const curationGroupIdSet = new Set(curationGroups.map(_temp2));
  let t17;
  bb0: {
    if (!rawCuratedByGroupId) {
      t17 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t17 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t17 = rawCuratedByGroupId;
      break bb0;
    }
    t17 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t17;
  let t18;
  bb1: {
    const hasPriceFilter = typeof minPrice === "number" || typeof maxPrice === "number";
    if (isCurationWave && (hasPriceFilter || sort === WaveDropsLeaderboardSort.PRICE)) {
      t18 = "ETH";
      break bb1;
    }
    t18 = undefined;
  }
  const priceCurrency = t18;
  let t19;
  if ($[32] !== pathname || $[33] !== router || $[34] !== searchParams) {
    t19 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curation_id", groupId);
      } else {
        nextParams.delete("curation_id");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[32] = pathname;
    $[33] = router;
    $[34] = searchParams;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const updateCurationGroupInUrl = t19;
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = t21 => {
      const {
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      } = t21;
      setMinPrice(nextMinPrice);
      setMaxPrice(nextMaxPrice);
    };
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const updatePriceRange = t20;
  let t21;
  bb2: {
    if (!isMemesWave) {
      t21 = viewMode;
      break bb2;
    }
    if (viewMode === "grid_content_only") {
      t21 = "grid";
      break bb2;
    }
    t21 = viewMode;
  }
  const effectiveViewMode = t21;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    const t22 = canOpenCreateDrop ? onCreateDrop : undefined;
    let t23;
    if ($[37] !== curatedByGroupId || $[38] !== isApprovalVotingClosed || $[39] !== isApprovalVotingControlsLocked || $[40] !== maxPrice || $[41] !== minPrice || $[42] !== onDropClick || $[43] !== priceCurrency || $[44] !== sort || $[45] !== t22 || $[46] !== wave) {
      t23 = <WaveLeaderboardDrops wave={wave} sort={sort} isVotingClosed={isApprovalVotingClosed} isVotingControlsLocked={isApprovalVotingControlsLocked} curatedByGroupId={curatedByGroupId} onDropClick={onDropClick} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={t22} />;
      $[37] = curatedByGroupId;
      $[38] = isApprovalVotingClosed;
      $[39] = isApprovalVotingControlsLocked;
      $[40] = maxPrice;
      $[41] = minPrice;
      $[42] = onDropClick;
      $[43] = priceCurrency;
      $[44] = sort;
      $[45] = t22;
      $[46] = wave;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    leaderboardContent = t23;
  } else {
    if (!isMemesWave) {
      const t22 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t23;
      if ($[48] !== curatedByGroupId || $[49] !== isApprovalVotingClosed || $[50] !== isApprovalVotingControlsLocked || $[51] !== maxPrice || $[52] !== minPrice || $[53] !== onDropClick || $[54] !== priceCurrency || $[55] !== sort || $[56] !== t22 || $[57] !== wave) {
        t23 = <WaveLeaderboardGrid wave={wave} sort={sort} isVotingClosed={isApprovalVotingClosed} isVotingControlsLocked={isApprovalVotingControlsLocked} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} mode={t22} onDropClick={onDropClick} />;
        $[48] = curatedByGroupId;
        $[49] = isApprovalVotingClosed;
        $[50] = isApprovalVotingControlsLocked;
        $[51] = maxPrice;
        $[52] = minPrice;
        $[53] = onDropClick;
        $[54] = priceCurrency;
        $[55] = sort;
        $[56] = t22;
        $[57] = wave;
        $[58] = t23;
      } else {
        t23 = $[58];
      }
      leaderboardContent = t23;
    } else {
      let t22;
      if ($[59] !== curatedByGroupId || $[60] !== isApprovalVotingClosed || $[61] !== isApprovalVotingControlsLocked || $[62] !== maxPrice || $[63] !== minPrice || $[64] !== onDropClick || $[65] !== priceCurrency || $[66] !== sort || $[67] !== wave) {
        t22 = <WaveLeaderboardGallery wave={wave} sort={sort} isVotingClosed={isApprovalVotingClosed} isVotingControlsLocked={isApprovalVotingControlsLocked} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onDropClick={onDropClick} />;
        $[59] = curatedByGroupId;
        $[60] = isApprovalVotingClosed;
        $[61] = isApprovalVotingControlsLocked;
        $[62] = maxPrice;
        $[63] = minPrice;
        $[64] = onDropClick;
        $[65] = priceCurrency;
        $[66] = sort;
        $[67] = wave;
        $[68] = t22;
      } else {
        t22 = $[68];
      }
      leaderboardContent = t22;
    }
  }
  let t22;
  if ($[69] !== approvalCloseStatus || $[70] !== approvedCount || $[71] !== isApprovalStatusError || $[72] !== isApproveWave || $[73] !== retryApprovalStatus || $[74] !== wave) {
    t22 = isApproveWave ? <WaveApprovalStatusBar approvedCount={approvedCount} closeStatus={approvalCloseStatus} isApprovalStatusError={isApprovalStatusError} retryApprovalStatus={retryApprovalStatus} wave={wave} /> : <WaveLeaderboardTime wave={wave} />;
    $[69] = approvalCloseStatus;
    $[70] = approvedCount;
    $[71] = isApprovalStatusError;
    $[72] = isApproveWave;
    $[73] = retryApprovalStatus;
    $[74] = wave;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== setViewMode) {
    t23 = mode => setViewMode(mode);
    $[76] = setViewMode;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  const t24 = canOpenCreateDrop ? onCreateDrop : undefined;
  let t25;
  if ($[78] !== setSort) {
    t25 = s => setSort(s);
    $[78] = setSort;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  const t26 = curatedByGroupId ?? null;
  const t27 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  const t28 = isCurationWave ? updatePriceRange : undefined;
  let t29;
  if ($[80] !== curationGroups || $[81] !== effectiveViewMode || $[82] !== maxPrice || $[83] !== minPrice || $[84] !== sort || $[85] !== t23 || $[86] !== t24 || $[87] !== t25 || $[88] !== t26 || $[89] !== t27 || $[90] !== t28 || $[91] !== wave) {
    t29 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t23} onCreateDrop={t24} onSortChange={t25} curationGroups={curationGroups} curatedByGroupId={t26} onCurationGroupChange={t27} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={t28} /></div>;
    $[80] = curationGroups;
    $[81] = effectiveViewMode;
    $[82] = maxPrice;
    $[83] = minPrice;
    $[84] = sort;
    $[85] = t23;
    $[86] = t24;
    $[87] = t25;
    $[88] = t26;
    $[89] = t27;
    $[90] = t28;
    $[91] = wave;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== showToggleableDropInput || $[94] !== wave) {
    t30 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={closeCreateDrop} onSuccess={closeCreateDrop} /></motion.div>;
    $[93] = showToggleableDropInput;
    $[94] = wave;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let t31;
  if ($[96] !== t30) {
    t31 = <AnimatePresence>{t30}</AnimatePresence>;
    $[96] = t30;
    $[97] = t31;
  } else {
    t31 = $[97];
  }
  let t32;
  if ($[98] !== activeCreateDropExperience || $[99] !== wave) {
    t32 = activeCreateDropExperience === WaveSubmissionExperience.MEMES_LEGACY && <MemesArtSubmissionModal isOpen={true} wave={wave} onClose={closeCreateDrop} />;
    $[98] = activeCreateDropExperience;
    $[99] = wave;
    $[100] = t32;
  } else {
    t32 = $[100];
  }
  let t33;
  if ($[101] !== activeCreateDropExperience || $[102] !== wave) {
    t33 = activeCreateDropExperience === WaveSubmissionExperience.CURATION_LEGACY && <WaveLeaderboardCurationDropModal isOpen={true} wave={wave} onClose={closeCreateDrop} />;
    $[101] = activeCreateDropExperience;
    $[102] = wave;
    $[103] = t33;
  } else {
    t33 = $[103];
  }
  let t34;
  if ($[104] !== activeCreateDropExperience || $[105] !== wave) {
    t34 = activeCreateDropExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && <WaveDropCreate wave={wave} onCancel={closeCreateDrop} onSuccess={closeCreateDrop} />;
    $[104] = activeCreateDropExperience;
    $[105] = wave;
    $[106] = t34;
  } else {
    t34 = $[106];
  }
  let t35;
  if ($[107] !== leaderboardContent || $[108] !== t31 || $[109] !== t32 || $[110] !== t33 || $[111] !== t34) {
    t35 = <div className="tw-min-w-0 tw-pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]">{t31}{t32}{t33}{t34}{leaderboardContent}</div>;
    $[107] = leaderboardContent;
    $[108] = t31;
    $[109] = t32;
    $[110] = t33;
    $[111] = t34;
    $[112] = t35;
  } else {
    t35 = $[112];
  }
  let t36;
  if ($[113] !== leaderboardViewStyle || $[114] !== t22 || $[115] !== t29 || $[116] !== t35) {
    t36 = <div className="tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t22}{t29}{t35}</div>;
    $[113] = leaderboardViewStyle;
    $[114] = t22;
    $[115] = t29;
    $[116] = t35;
    $[117] = t36;
  } else {
    t36 = $[117];
  }
  return t36;
};
export default MyStreamWaveLeaderboard;
function _temp(current) {
  if (current.submissionExperience === null) {
    return current;
  }
  return {
    ...current,
    submissionExperience: null
  };
}
function _temp2(group) {
  return group.id;
}